import './index.css';
import { jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import { match } from '@hh.ru/magritte-common-fuzzy-search';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { ItemsList } from './ItemsList.js';
import { MobileItemsList } from './MobileItemsList.js';
import { TreeSelectorWrapper } from './TreeSelectorWrapper.js';
import { filterWithParents } from './collection/treeCollectionHelper.js';
import { useExpanded } from './useExpanded.js';
import { useIndeterminate } from './useIndeterminate.js';
import { useRenderInput } from './useRenderInput.js';
import '@tanstack/react-virtual';
import './Item.js';
import 'classnames';
import './ItemContent.js';
import '@hh.ru/magritte-common-keyboard';
import '@hh.ru/magritte-ui-icon/icon';
import './Action.js';
import '@hh.ru/magritte-ui-checkbox-radio';
import './TreeSelectorItemBase.js';
import '@hh.ru/magritte-ui-cell';
import '@hh.ru/magritte-ui-typography';
import './tree-selector-item-B-I_mxX1.js';
import '@hh.ru/magritte-common-func-utils';
import '@hh.ru/magritte-ui-checkable-card/CheckableCardElement';
import './MobileDelimiter.js';
import '@hh.ru/magritte-ui-card';
import './MobileItem.js';
import './MobileParentItem.js';
import '@hh.ru/magritte-ui-badge';
import '@hh.ru/magritte-common-use-multiple-refs';
import '@hh.ru/magritte-internal-custom-scroll';
import './collection/treeCollection.js';
import '@hh.ru/magritte-ui-input';
import './useAnimationTimeout.js';

const defaultCheckSelectable = () => true;
const defaultArray = [];
const needToApply = (query) => {
    return !!(query && query.length);
};
const TreeSelectorDummyComponent = ({ collection: initialCollection, checkSelectable = defaultCheckSelectable, initialExpanded = defaultArray, value: selected, leavesOnly, singleChoice, disabled = defaultArray, treeFilter = filterWithParents, suggestedNotFound = defaultArray, constantlySuggested = defaultArray, renderItemForDesktop, renderItem, renderMobileDelimiter, onExpand, onChange, onChangeFilterQuery, contentNotFound, onMobileNavigationChange, getMobileSearchItemOrder, expandTreeOnSelected = true, getSelectAllParentTrl, renderContentBefore, children, }, ref) => {
    const [collection, setCollection] = useState(initialCollection);
    const [isSearch, setIsSearch] = useState(false);
    const currentQuery = useRef('');
    const inputRef = useRef(null);
    const suggestedNotFoundModels = useRef(initialCollection.getExistModels(suggestedNotFound));
    const constantlySuggestedModels = useRef(initialCollection.getExistModels(constantlySuggested));
    const getSearchItemOrderDefault = useCallback((model) => {
        return initialCollection.getModelLevel(model.id);
    }, [initialCollection]);
    const { indeterminate } = useIndeterminate({ collection: initialCollection, selected });
    const { expanded: currentExpanded, setExpanded, handleExpansion, handleResetExpanded, } = useExpanded({
        initialValue: initialExpanded?.slice(),
        selected,
        expandTreeOnSelected,
        collection: initialCollection,
        onExpand,
    });
    const { isMobile } = useBreakpoint();
    const { contentFilterQuery, setInputValue, handleChangeInput, renderInput } = useRenderInput(inputRef);
    const treeRefCallback = useCallback((node) => {
        if (!node) {
            handleResetExpanded();
        }
    }, [handleResetExpanded]);
    useEffect(() => {
        const contentFilterQueryTrimmed = contentFilterQuery.trim();
        const queryWasChanged = contentFilterQueryTrimmed !== currentQuery.current.trim();
        if (queryWasChanged && needToApply(contentFilterQueryTrimmed)) {
            const newExpanded = [];
            const newCollection = treeFilter(initialCollection, (item) => match(contentFilterQueryTrimmed, item.text), isMobile);
            newCollection.toList().forEach((item) => {
                if (newCollection.hasChildren(item.id)) {
                    // Если есть в отфильтрованной коллекции есть дочерние элементы,
                    // значит они заматчились, и нужно открыть родителя.
                    newExpanded.push(item.id);
                }
                else {
                    // Если заматчился только сам родитель, показываем его
                    // схлопнутым => нужно добавить его ветку в коллекцию.
                    initialCollection.walkChildren(item.id, (child, parents) => {
                        newCollection.addModel({ ...child }, parents[0].id);
                    });
                }
            });
            const filteredIds = newCollection.toList().map((model) => model.id);
            // Если в отфильтрованной коллекции нет моделей, но заданы предложенные,
            // то показываем их
            // Если в отфильтрованной коллекции модели есть, то пробуем добавить к ним
            // всегда показывающиеся модели, если их ещё нет в коллекции
            if (!filteredIds.length && suggestedNotFoundModels.current.length) {
                suggestedNotFoundModels.current.forEach((model) => newCollection.addModel({ ...model }));
            }
            else {
                constantlySuggestedModels.current.forEach((model) => {
                    if (!filteredIds.includes(model.id)) {
                        newCollection.addModel({ ...model });
                    }
                });
            }
            setCollection(newCollection);
            currentQuery.current = contentFilterQuery;
            onChangeFilterQuery?.(filteredIds, contentFilterQueryTrimmed);
            setExpanded(newExpanded);
            setIsSearch(true);
        }
        else if (queryWasChanged) {
            // Запрос не годится для поиска.
            const newExpanded = initialExpanded.slice();
            setCollection(initialCollection);
            onChangeFilterQuery?.(initialCollection.toList().map((model) => model.id), contentFilterQueryTrimmed);
            currentQuery.current = contentFilterQuery;
            if (inputRef.current) {
                setExpanded(newExpanded);
            }
            else {
                handleResetExpanded();
            }
            setIsSearch(false);
        }
    }, [
        initialCollection,
        contentFilterQuery,
        treeFilter,
        selected,
        setExpanded,
        isMobile,
        onChangeFilterQuery,
        handleResetExpanded,
        initialExpanded,
    ]);
    const renderTreeSelector = useCallback(() => {
        const hasModels = collection.getTopLevel().length;
        return (jsx(TreeSelectorWrapper, { forwardRef: treeRefCallback, children: hasModels ? (jsx(Fragment, { children: isMobile ? (jsx(MobileItemsList, { getMobileSearchItemOrder: getMobileSearchItemOrder || getSearchItemOrderDefault, collection: collection, selected: selected, disabled: disabled, onChange: onChange, leavesOnly: leavesOnly, checkSelectable: checkSelectable, singleChoice: singleChoice, ref: ref, setInputValue: setInputValue, renderItem: renderItem, renderMobileDelimiter: renderMobileDelimiter, handleChangeInput: handleChangeInput, contentFilterQuery: contentFilterQuery.trim(), onMobileNavigationChange: onMobileNavigationChange, isSearch: isSearch, getSelectAllParentTrl: getSelectAllParentTrl, renderContentBefore: renderContentBefore })) : (jsx(ItemsList, { collection: collection, leavesOnly: leavesOnly, checkSelectable: checkSelectable, expanded: currentExpanded, onExpansion: handleExpansion, selected: selected, onChange: onChange, disabled: disabled, singleChoice: singleChoice, indeterminate: indeterminate, isSearch: isSearch, renderItemForDesktop: renderItemForDesktop, renderContentBefore: renderContentBefore })) })) : (jsx(Fragment, { children: contentNotFound })) }));
    }, [
        collection,
        treeRefCallback,
        isMobile,
        getMobileSearchItemOrder,
        getSearchItemOrderDefault,
        selected,
        disabled,
        onChange,
        leavesOnly,
        checkSelectable,
        singleChoice,
        ref,
        setInputValue,
        renderItem,
        renderMobileDelimiter,
        handleChangeInput,
        contentFilterQuery,
        onMobileNavigationChange,
        isSearch,
        getSelectAllParentTrl,
        renderContentBefore,
        currentExpanded,
        handleExpansion,
        indeterminate,
        renderItemForDesktop,
        contentNotFound,
    ]);
    return jsx(Fragment, { children: children({ renderTreeSelector, renderInput }) });
};
const TreeSelectorDummy = forwardRef(TreeSelectorDummyComponent);

export { TreeSelectorDummy, TreeSelectorDummyComponent };
//# sourceMappingURL=TreeSelectorDummy.js.map
